// src/pages/VerifyEmailPage.js

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Button,
  Alert
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../utils/apiConfig';

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setError('Kein Verifizierungs-Token gefunden');
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.get(
          getApiUrl(`/api/auth/verify-email/${token}`)
        );

        if (response.data.success) {
          setSuccess(true);
        }
      } catch (err) {
        console.error('Verification error:', err);
        setError(
          err.response?.data?.message || 
          'Verifizierung fehlgeschlagen. Der Link könnte abgelaufen sein.'
        );
      } finally {
        setVerifying(false);
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f2942 0%, #1a365d 50%, #2d3748 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: 5,
            borderRadius: 4,
            border: '2px solid rgba(0, 188, 212, 0.2)',
            background: 'linear-gradient(to bottom, #ffffff, #f8fafc)',
            textAlign: 'center'
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 4 }}>
            <AccountBalanceIcon sx={{ fontSize: 64, color: '#00bcd4', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a365d' }}>
              Immo-Automation
            </Typography>
          </Box>

          {verifying && (
            <>
              <CircularProgress 
                size={60} 
                sx={{ color: '#00bcd4', mb: 3 }} 
              />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a365d', mb: 2 }}>
                E-Mail wird verifiziert...
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Bitte warten Sie einen Moment
              </Typography>
            </>
          )}

          {!verifying && success && (
            <>
              <CheckCircleIcon 
                sx={{ 
                  fontSize: 80, 
                  color: '#10b981', 
                  mb: 3 
                }} 
              />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a365d', mb: 2 }}>
                Erfolgreich verifiziert! 🎉
              </Typography>
              <Typography variant="body1" sx={{ color: '#64748b', mb: 4 }}>
                Ihre E-Mail-Adresse wurde erfolgreich bestätigt. Sie können sich jetzt anmelden und mit Ihrer Immobilienverwaltung starten!
              </Typography>
              
              <Alert severity="success" sx={{ mb: 3 }}>
                <strong>🎁 25€ Startguthaben</strong> wurde Ihrem Account gutgeschrieben!
              </Alert>

              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/login')}
                sx={{
                  bgcolor: '#1a365d',
                  py: 2,
                  px: 6,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: '#0f2942'
                  }
                }}
              >
                Jetzt anmelden
              </Button>
            </>
          )}

          {!verifying && error && (
            <>
              <ErrorIcon 
                sx={{ 
                  fontSize: 80, 
                  color: '#ef4444', 
                  mb: 3 
                }} 
              />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a365d', mb: 2 }}>
                Verifizierung fehlgeschlagen
              </Typography>
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                {error}
              </Alert>
              
              <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
                Mögliche Gründe:
              </Typography>
              <Box component="ul" sx={{ textAlign: 'left', color: '#64748b', mb: 3 }}>
                <li>Der Verifizierungs-Link ist abgelaufen (24h Gültigkeit)</li>
                <li>Die E-Mail-Adresse wurde bereits verifiziert</li>
                <li>Der Link wurde bereits verwendet</li>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/register')}
                  sx={{
                    borderColor: '#1a365d',
                    color: '#1a365d',
                    '&:hover': {
                      borderColor: '#0f2942',
                      bgcolor: 'rgba(26, 54, 93, 0.04)'
                    }
                  }}
                >
                  Neu registrieren
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate('/kontakt')}
                  sx={{
                    bgcolor: '#1a365d',
                    '&:hover': {
                      bgcolor: '#0f2942'
                    }
                  }}
                >
                  Support kontaktieren
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default VerifyEmailPage;
